import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, GripVertical,
  EyeOff, CalendarDays, Plus, X, Filter, ArrowUpRight,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

/* Available data sources for the calendar */
const SOURCE_CATALOG = [
  { id: "events",     label: "Events",     accent: "#0A0A0A" },
  { id: "tasks",      label: "Tasks",      accent: "#B45309" },
  { id: "rehearsals", label: "Rehearsals", accent: "#7C3AED" },
  { id: "timeline",   label: "Schedule",   accent: "#059669" },
  { id: "custom",     label: "Custom",     accent: "#DC2626" },
];

const DEFAULT_SOURCES = SOURCE_CATALOG.map((s) => s.id);
const VIEW_MODES = ["month", "week", "day"];
/*
 * These two `localStorage` keys persist non-sensitive UI preferences only —
 * the active view (month/week/day) and which source chips are visible. No
 * user data, tokens, or PII touches them. This is the standard pattern for
 * UI-only preferences; sensitive data goes through the httpOnly `session_token`
 * cookie handled by the backend.
 */
const LS_KEY_SOURCES = "atelis.calendar.sources";
const LS_KEY_VIEW = "atelis.calendar.view";

/* Utilities */
const toISODate = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
};
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfWeek = (d) => { // Sunday-start
  const day = d.getDay();
  return addDays(d, -day);
};
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

/* -------------------------------------------------------------------------- */

export default function CalendarWidget({
  editing, collapsed, dragHandleProps, onToggleCollapse, onHide,
}) {
  const [view, setView] = useState(() => localStorage.getItem(LS_KEY_VIEW) || "month");
  const [cursor, setCursor] = useState(() => new Date());
  const [sources, setSources] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY_SOURCES);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_SOURCES;
    } catch { return DEFAULT_SOURCES; }
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // custom entry being edited
  const [filterOpen, setFilterOpen] = useState(false);

  // Range window fetched from the server — always a bit wider than the visible
  // view so keyboard nav + short zooms don't re-fetch on every click.
  const range = useMemo(() => {
    if (view === "day") {
      const d0 = addDays(cursor, -3);
      const d1 = addDays(cursor, 3);
      return { start: toISODate(d0), end: toISODate(d1) };
    }
    if (view === "week") {
      const s = startOfWeek(cursor);
      return { start: toISODate(addDays(s, -7)), end: toISODate(addDays(s, 13)) };
    }
    // month — pad by 1 week on each side (covers the grid overflow rows too)
    const s = addDays(startOfMonth(cursor), -14);
    const e = addDays(endOfMonth(cursor), 14);
    return { start: toISODate(s), end: toISODate(e) };
  }, [cursor, view]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar", {
        params: {
          start: range.start,
          end: range.end,
          sources: sources.join(","),
        },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error("Calendar load failed:", e);
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, sources]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => { localStorage.setItem(LS_KEY_VIEW, view); }, [view]);
  useEffect(() => { localStorage.setItem(LS_KEY_SOURCES, JSON.stringify(sources)); }, [sources]);

  const toggleSource = (id) => {
    setSources((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const goPrev = () => {
    if (view === "day") setCursor((c) => addDays(c, -1));
    else if (view === "week") setCursor((c) => addDays(c, -7));
    else setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  };
  const goNext = () => {
    if (view === "day") setCursor((c) => addDays(c, 1));
    else if (view === "week") setCursor((c) => addDays(c, 7));
    else setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  };
  const goToday = () => setCursor(new Date());
  const jumpTo = (v) => { const d = parseISO(v); if (d) setCursor(d); };

  const headerLabel = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(cursor);
      const e = addDays(s, 6);
      const sameMonth = s.getMonth() === e.getMonth();
      return `${MONTH_LABELS[s.getMonth()]} ${s.getDate()} – ${sameMonth ? "" : MONTH_LABELS[e.getMonth()] + " "}${e.getDate()}, ${e.getFullYear()}`;
    }
    return `${cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}`;
  }, [cursor, view]);

  const isCollapsed = !!collapsed;

  return (
    <section
      data-testid="widget-calendar"
      className={`bg-surface border ${editing ? "border-dashed border-[#F59E0B]" : "border-hair"}`}
    >
      {/* Header row */}
      <header className={`flex items-center justify-between gap-3 px-4 py-3 border-b ${editing ? "border-[#3F3F46]" : "border-hair"}`}>
        <div className="flex items-center gap-2 min-w-0">
          {editing && dragHandleProps && (
            <button
              type="button"
              {...dragHandleProps}
              data-testid="drag-handle-widget-calendar"
              title="Drag to reorder"
              className="cursor-grab active:cursor-grabbing text-[#F59E0B] hover:text-[#FCD34D]"
              aria-label="Drag calendar widget"
            >
              <GripVertical size={14} strokeWidth={2} />
            </button>
          )}
          <CalendarDays size={14} strokeWidth={2} className="text-faint" />
          <h3 className="font-display text-sm font-bold text-app tracking-tight uppercase truncate">
            Calendar
          </h3>
          <span className="font-mono text-[10px] tabular-nums text-faint">[{String(items.length).padStart(2, "0")}]</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {!isCollapsed && (
            <>
              {/* View toggle */}
              <div className="inline-flex border border-hair font-mono text-[10px] tracking-[0.15em] uppercase" role="tablist" aria-label="Calendar view">
                {VIEW_MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setView(m)}
                    data-testid={`calendar-view-${m}`}
                    aria-pressed={view === m}
                    className={`px-2.5 h-7 border-r border-hair last:border-r-0 ${view === m ? "bevel-btn-primary text-white" : "text-muted hover:bg-surface-2"}`}
                  >
                    {m}
                  </button>
                ))}
              </div>

              {/* Jump to date */}
              <input
                type="date"
                value={toISODate(cursor)}
                onChange={(e) => jumpTo(e.target.value)}
                data-testid="calendar-jump-date"
                aria-label="Jump to date"
                className="h-7 bevel-inset border border-hair rounded-md font-mono text-[10px] tracking-widest uppercase px-2 text-app focus:outline-none focus:border-[#06B6D4]"
              />

              {/* Source filter (dropdown-style; opens a chip row on click) */}
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                data-testid="calendar-filter-toggle"
                aria-pressed={filterOpen}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 border font-mono text-[10px] tracking-[0.15em] uppercase ${filterOpen ? "border-[#06B6D4] bg-surface-2 text-app" : "border-hair text-muted hover:border-hair-emph"}`}
              >
                <Filter size={11} strokeWidth={2} /> {sources.length}/{SOURCE_CATALOG.length}
              </button>

              <button
                type="button"
                onClick={() => setAddOpen(true)}
                data-testid="calendar-add-entry-btn"
                title="Add custom entry"
                className="inline-flex items-center gap-1.5 h-7 px-2.5 border border-hair-emph bg-surface-2 text-app font-mono text-[10px] tracking-[0.15em] uppercase hover:border-[#06B6D4] btn-primary-glow"
              >
                <Plus size={11} strokeWidth={2} /> Entry
              </button>
            </>
          )}

          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              data-testid="toggle-collapse-widget-calendar"
              title={isCollapsed ? "Expand" : "Collapse"}
              className="text-faint hover:text-app"
              aria-label={isCollapsed ? "Expand widget" : "Collapse widget"}
            >
              {isCollapsed ? <ChevronDown size={13} strokeWidth={2} /> : <ChevronUp size={13} strokeWidth={2} />}
            </button>
          )}
          {editing && onHide && (
            <button
              type="button"
              onClick={onHide}
              data-testid="hide-widget-widget-calendar"
              title="Hide widget"
              className="text-faint hover:text-[#EF4444]"
              aria-label="Hide widget"
            >
              <EyeOff size={13} strokeWidth={2} />
            </button>
          )}
        </div>
      </header>

      {isCollapsed ? null : (
        <>
          {/* Source filter row (only when open) */}
          {filterOpen && (
            <div className="px-4 py-2 border-b border-hair bg-inset flex items-center gap-2 flex-wrap" data-testid="calendar-filter-row">
              {SOURCE_CATALOG.map((s) => {
                const on = sources.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleSource(s.id)}
                    data-testid={`calendar-source-${s.id}`}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 h-6 px-2 border font-mono text-[10px] tracking-[0.15em] uppercase ${on ? "border-hair-emph bg-surface text-app" : "border-hair bg-surface text-faint"}`}
                  >
                    <span className="w-2 h-2 flex-shrink-0" style={{ background: on ? s.accent : "#3F3F46" }} />
                    {s.label}
                  </button>
                );
              })}
              <span className="ml-auto font-mono text-[10px] tracking-widest uppercase text-faint">
                Filter sources
              </span>
            </div>
          )}

          {/* Sub-header with prev/next + label */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-hair">
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={goPrev} data-testid="calendar-prev-btn"
                className="h-7 w-7 border border-hair hover:border-hair-emph flex items-center justify-center text-muted"
                aria-label="Previous">
                <ChevronLeft size={14} strokeWidth={2} />
              </button>
              <button type="button" onClick={goToday} data-testid="calendar-today-btn"
                className="h-7 px-2.5 border border-hair hover:border-hair-emph font-mono text-[10px] tracking-widest uppercase text-muted">
                Today
              </button>
              <button type="button" onClick={goNext} data-testid="calendar-next-btn"
                className="h-7 w-7 border border-hair hover:border-hair-emph flex items-center justify-center text-muted"
                aria-label="Next">
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
            <div className="font-display text-lg font-black text-app tracking-tight uppercase" data-testid="calendar-header-label">
              {headerLabel}
            </div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-faint">
              {loading ? "LOADING…" : `${items.length} ITEM${items.length === 1 ? "" : "S"}`}
            </div>
          </div>

          {/* View body */}
          <div className="p-4">
            {view === "month" && (
              <MonthGrid
                cursor={cursor}
                items={items}
                onEditCustom={(entry) => { setEditingEntry(entry); setAddOpen(true); }}
              />
            )}
            {view === "week" && (
              <WeekGrid
                cursor={cursor}
                items={items}
                onEditCustom={(entry) => { setEditingEntry(entry); setAddOpen(true); }}
              />
            )}
            {view === "day" && (
              <DayList
                cursor={cursor}
                items={items}
                onEditCustom={(entry) => { setEditingEntry(entry); setAddOpen(true); }}
              />
            )}
          </div>

          {/* Legend */}
          <footer className="px-4 py-2 border-t border-hair flex items-center gap-3 flex-wrap font-mono text-[9px] tracking-widest uppercase text-faint">
            <span>LEGEND</span>
            {SOURCE_CATALOG.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2" style={{ background: s.accent }} />
                {s.label}
              </span>
            ))}
          </footer>
        </>
      )}

      <EntryDialog
        open={addOpen}
        onClose={() => { setAddOpen(false); setEditingEntry(null); }}
        entry={editingEntry}
        defaultDate={toISODate(cursor)}
        onSaved={loadItems}
      />
    </section>
  );
}

/* ==========================================================================
 * MONTH GRID
 * ==========================================================================*/
function MonthGrid({ cursor, items, onEditCustom }) {
  const gridStart = startOfWeek(startOfMonth(cursor));
  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) arr.push(addDays(gridStart, i));
    return arr;
  }, [gridStart]);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      const k = it.date;
      if (!k) continue;
      (map[k] = map[k] || []).push(it);
    }
    // stable-sort each bucket by time
    for (const k in map) map[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return map;
  }, [items]);

  const today = new Date();
  const curMonth = cursor.getMonth();

  return (
    <div data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 border-t border-l border-hair">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="font-mono text-[10px] tracking-widest uppercase text-faint px-2 py-1.5 border-r border-b border-hair bg-inset">
            {w}
          </div>
        ))}
        {cells.map((d) => {
          const iso = toISODate(d);
          const dayItems = byDay[iso] || [];
          const isToday = sameDay(d, today);
          const inMonth = d.getMonth() === curMonth;
          return (
            <div
              key={iso}
              data-testid={`calendar-cell-${iso}`}
              className={`min-h-[92px] border-r border-b border-hair p-1.5 relative ${inMonth ? "bg-surface" : "bg-inset"}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className={`font-mono text-[11px] tabular-nums ${isToday ? "text-white bg-[#EF4444] px-1.5" : inMonth ? "text-app" : "text-faint opacity-50"} ${isToday ? "font-semibold" : ""}`}>
                  {d.getDate()}
                </span>
                {isToday && (
                  <span className="font-mono text-[8px] tracking-widest uppercase text-[#F87171] live-pulse">NOW</span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayItems.slice(0, 3).map((it) => (
                  <CalendarPill key={it.id} item={it} onEditCustom={onEditCustom} />
                ))}
                {dayItems.length > 3 && (
                  <div className="font-mono text-[9px] tracking-widest uppercase text-faint px-1">
                    +{dayItems.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ==========================================================================
 * WEEK GRID
 * ==========================================================================*/
function WeekGrid({ cursor, items, onEditCustom }) {
  const s = startOfWeek(cursor);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(s, i)), [s]);
  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!it.date) continue;
      (map[it.date] = map[it.date] || []).push(it);
    }
    for (const k in map) map[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return map;
  }, [items]);
  const today = new Date();

  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="calendar-week-grid">
      {days.map((d) => {
        const iso = toISODate(d);
        const dayItems = byDay[iso] || [];
        const isToday = sameDay(d, today);
        return (
          <div
            key={iso}
            data-testid={`calendar-week-cell-${iso}`}
            className={`border border-hair min-h-[180px] bg-surface`}
          >
            <div className={`flex items-baseline justify-between px-2 py-1.5 border-b border-hair ${isToday ? "bg-aurora" : "bg-inset"}`}>
              <span className={`font-mono text-[10px] tracking-widest uppercase ${isToday ? "text-white" : "text-faint"}`}>
                {WEEKDAY_LABELS[d.getDay()]}
              </span>
              <span className={`font-mono text-sm tabular-nums font-semibold ${isToday ? "text-white" : "text-app"}`}>
                {d.getDate()}
              </span>
            </div>
            <div className="p-1.5 space-y-1">
              {dayItems.length === 0 ? (
                <div className="font-mono text-[10px] tracking-widest uppercase text-faint opacity-60 py-2 text-center">—</div>
              ) : dayItems.map((it) => (
                <CalendarPill key={it.id} item={it} full onEditCustom={onEditCustom} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ==========================================================================
 * DAY LIST
 * ==========================================================================*/
function DayList({ cursor, items, onEditCustom }) {
  const iso = toISODate(cursor);
  const dayItems = items.filter((it) => it.date === iso);
  dayItems.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const today = sameDay(cursor, new Date());

  return (
    <div data-testid="calendar-day-list">
      <div className={`border-l-2 ${today ? "border-[#EF4444]" : "border-hair-emph"} pl-4 py-2 mb-3`}>
        <div className="font-mono text-[10px] tracking-widest uppercase text-faint">
          {today ? "TODAY" : cursor.toLocaleDateString(undefined, { weekday: "long" }).toUpperCase()}
        </div>
        <div className="font-display text-2xl font-black text-app">{cursor.getDate()} {MONTH_LABELS[cursor.getMonth()]} {cursor.getFullYear()}</div>
      </div>
      {dayItems.length === 0 ? (
        <div className="border border-dashed border-hair py-8 text-center">
          <div className="font-mono text-[10px] tracking-widest uppercase text-faint">NOTHING SCHEDULED</div>
          <div className="text-xs text-faint opacity-70 mt-1">Add a custom entry to block off your time.</div>
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--hair)] border border-hair">
          {dayItems.map((it) => (
            <li key={it.id}>
              <CalendarRow item={it} onEditCustom={onEditCustom} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ==========================================================================
 * PILL & ROW RENDERERS
 * ==========================================================================*/
function CalendarPill({ item, full, onEditCustom }) {
  const color = item.color || "#FAFAFA";
  const showTime = !!item.time && !item.all_day;
  const body = (
    <>
      <span className="w-1 self-stretch flex-shrink-0" style={{ background: color }} />
      <span className="flex-1 min-w-0 flex items-center gap-1">
        {showTime && (
          <span className="font-mono text-[9px] tabular-nums text-muted flex-shrink-0">{item.time}</span>
        )}
        <span className="truncate text-[11px] text-app leading-tight">{item.title}</span>
      </span>
    </>
  );

  const testId = `calendar-item-${item.id}`;
  const commonClass = `flex items-stretch gap-1 px-1 py-0.5 border border-hair hover:border-hair-emph bg-surface-2 ${full ? "w-full" : ""}`;

  if (item.source === "custom") {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => onEditCustom?.(item)}
        className={commonClass + " text-left"}
        title={`${item.title} — click to edit`}
      >
        {body}
      </button>
    );
  }
  if (item.event_id) {
    return (
      <Link
        to={`/events/${item.event_id}`}
        data-testid={testId}
        className={commonClass + " group"}
        title={`${item.title}${item.event_title ? " · " + item.event_title : ""}`}
      >
        {body}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={commonClass}>{body}</div>
  );
}

function CalendarRow({ item, onEditCustom }) {
  const color = item.color || "#FAFAFA";
  const isCustom = item.source === "custom";
  const inner = (
    <>
      <span className="w-1 self-stretch flex-shrink-0" style={{ background: color }} />
      <div className="flex-1 min-w-0 py-2 pr-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono text-[10px] tracking-widest uppercase text-faint">{item.source.toUpperCase()}</span>
          {item.time && <span className="font-mono text-xs tabular-nums text-muted">{item.time}</span>}
          <span className="text-sm font-semibold text-app truncate">{item.title}</span>
          {item.event_title && item.source !== "event" && (
            <span className="text-xs text-faint truncate">· {item.event_title}</span>
          )}
        </div>
        {item.description && (
          <div className="text-xs text-muted mt-1 line-clamp-2">{item.description}</div>
        )}
      </div>
      {(item.event_id || isCustom) && (
        <ArrowUpRight size={13} strokeWidth={2} className="text-faint mr-2 self-center flex-shrink-0" />
      )}
    </>
  );

  const testId = `calendar-row-${item.id}`;
  if (isCustom) {
    return (
      <button
        type="button"
        onClick={() => onEditCustom?.(item)}
        data-testid={testId}
        className="flex items-stretch w-full hover:bg-surface-2 text-left"
      >
        {inner}
      </button>
    );
  }
  if (item.event_id) {
    return (
      <Link to={`/events/${item.event_id}`} data-testid={testId} className="flex items-stretch hover:bg-surface-2">
        {inner}
      </Link>
    );
  }
  return <div data-testid={testId} className="flex items-stretch">{inner}</div>;
}

/* ==========================================================================
 * ENTRY DIALOG (create / edit / delete custom entries)
 * ==========================================================================*/
const ENTRY_COLORS = [
  { hex: "#0A0A0A", label: "Ink" },
  { hex: "#DC2626", label: "Red" },
  { hex: "#B45309", label: "Amber" },
  { hex: "#059669", label: "Emerald" },
  { hex: "#2563EB", label: "Blue" },
  { hex: "#7C3AED", label: "Violet" },
  { hex: "#71717A", label: "Zinc" },
];

function EntryDialog({ open, onClose, entry, defaultDate, onSaved }) {
  // Existing custom entries surface with source==="custom" — pull their raw
  // entry_id for the PATCH/DELETE endpoints.
  const isEdit = !!entry && entry.source === "custom";
  const [form, setForm] = useState({
    title: "", date: defaultDate, time: "", end_time: "",
    all_day: true, color: "#0A0A0A", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const initedFor = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit && initedFor.current !== entry.id) {
      setForm({
        title: entry.title || "",
        date: entry.date || defaultDate,
        time: entry.time || "",
        end_time: entry.end_time || "",
        all_day: !!entry.all_day,
        color: entry.color || "#0A0A0A",
        notes: entry.description || "",
      });
      initedFor.current = entry.id;
    } else if (!isEdit && initedFor.current !== "new") {
      setForm({
        title: "", date: defaultDate, time: "", end_time: "",
        all_day: true, color: "#0A0A0A", notes: "",
      });
      initedFor.current = "new";
    }
  }, [open, isEdit, entry, defaultDate]);

  useEffect(() => { if (!open) initedFor.current = null; }, [open]);

  const save = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Add a title and date");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        time: form.all_day ? null : (form.time || null),
        end_time: form.all_day ? null : (form.end_time || null),
        all_day: form.all_day,
        color: form.color,
        notes: form.notes || null,
      };
      if (isEdit) {
        await api.patch(`/calendar/entries/${entry.entry_id || entry.id.replace(/^custom:/, "")}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/calendar/entries", payload);
        toast.success("Entry added");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("Entry save failed:", e);
      toast.error("Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    setSaving(true);
    try {
      await api.delete(`/calendar/entries/${entry.entry_id || entry.id.replace(/^custom:/, "")}`);
      toast.success("Entry deleted");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error("Could not delete");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md bg-surface-3 border-hair rounded-sm" data-testid="calendar-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-app">
            {isEdit ? "Edit entry" : "New calendar entry"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="eyebrow block mb-2">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="calendar-entry-title"
              placeholder="e.g. Site visit — Grand Terrace"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow block mb-2">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="calendar-entry-date"
              />
            </div>
            <div>
              <Label className="eyebrow block mb-2">All day</Label>
              <div className="h-10 flex items-center">
                <label className="inline-flex items-center gap-2 text-sm text-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.all_day}
                    onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                    data-testid="calendar-entry-allday"
                    className="h-4 w-4 accent-[#06B6D4]"
                  />
                  Whole day
                </label>
              </div>
            </div>
          </div>
          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="eyebrow block mb-2">Start</Label>
                <Input
                  type="time"
                  value={form.time || ""}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  data-testid="calendar-entry-time"
                />
              </div>
              <div>
                <Label className="eyebrow block mb-2">End</Label>
                <Input
                  type="time"
                  value={form.end_time || ""}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="calendar-entry-endtime"
                />
              </div>
            </div>
          )}
          <div>
            <Label className="eyebrow block mb-2">Color tag</Label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {ENTRY_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  aria-label={c.label}
                  onClick={() => setForm({ ...form, color: c.hex })}
                  data-testid={`calendar-entry-color-${c.hex.slice(1)}`}
                  className={`w-6 h-6 border ${form.color === c.hex ? "border-[#06B6D4] ring-2 ring-offset-1 ring-[#06B6D4]" : "border-hair"}`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>
          <div>
            <Label className="eyebrow block mb-2">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="calendar-entry-notes"
              className="rounded-sm"
              placeholder="Optional details…"
            />
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between gap-2">
          <div>
            {isEdit && (
              <Button
                variant="ghost"
                onClick={remove}
                disabled={saving}
                data-testid="calendar-entry-delete"
                className="text-[#EF4444] hover:text-[#FCA5A5]"
              >
                <X size={13} strokeWidth={2} className="mr-1" /> Delete
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="calendar-entry-cancel" disabled={saving}>Cancel</Button>
            <Button onClick={save} data-testid="calendar-entry-save" disabled={saving} className="rounded-sm bg-aurora hover:opacity-90 text-white btn-primary-glow">
              {isEdit ? "Save changes" : "Add entry"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
